import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

export default function InsuranceNewsDailyDigestEditPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { hasAnyRole } = useAuth()
    const { show } = useToast()
    const [digestDate, setDigestDate] = useState('')
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [pinned, setPinned] = useState(true)
    const [status, setStatus] = useState('')
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const isOperator = hasAnyRole && hasAnyRole([Roles.OPERATOR])

    useEffect(() => {
        if (!isOperator || !id) return
        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                const res = await api.get(`/insurance-news/daily-digests/${id}`)
                const row = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (cancelled || !row) return
                setDigestDate(row.digestDate || '')
                setTitle(row.title || '')
                setContent(row.content || '')
                setPinned(row.pinned !== false)
                setStatus(row.publicationStatus || '')
            } catch {
                if (!cancelled) show('불러오지 못했습니다', 'error')
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [id, isOperator, show])

    if (!isOperator) {
        return (
            <div className="p-8 text-center">
                <p className="text-red-600">권한이 없습니다.</p>
            </div>
        )
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (status !== 'PENDING') {
            show('승인 대기 상태만 수정할 수 있습니다', 'error')
            return
        }
        if (!digestDate || !title.trim() || !content.trim()) {
            show('대상 일자, 제목, 본문을 입력해주세요', 'error')
            return
        }
        setSubmitting(true)
        try {
            await api.patch(`/insurance-news/daily-digests/${id}`, {
                digestDate,
                title: title.trim(),
                content: content.trim(),
                pinned,
            })
            show('저장되었습니다', 'success')
            navigate('/work/insurance-news')
        } catch (err) {
            const message = err?.response?.data?.message || '저장에 실패했습니다'
            show(message, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return <div className="p-8 text-center text-slate-500">불러오는 중…</div>
    }

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">다이제스트 수정</h1>
                <button
                    type="button"
                    onClick={() => navigate('/work/insurance-news')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                >
                    목록으로
                </button>
            </div>
            {status && status !== 'PENDING' && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    이 다이제스트는 {status === 'PUBLISHED' ? '게시됨' : '반려됨'} 상태라 본문을 수정할 수 없습니다.
                </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border border-slate-200 bg-white p-6">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">대상 일자</label>
                    <input
                        type="date"
                        value={digestDate}
                        onChange={(e) => setDigestDate(e.target.value)}
                        disabled={status !== 'PENDING'}
                        className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">제목</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={status !== 'PENDING'}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                        maxLength={200}
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">본문</label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        disabled={status !== 'PENDING'}
                        rows={16}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono disabled:bg-slate-100"
                    />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={pinned}
                        onChange={(e) => setPinned(e.target.checked)}
                        disabled={status !== 'PENDING'}
                    />
                    게시 후 목록 상단 고정
                </label>
                {status === 'PENDING' && (
                    <button
                        type="submit"
                        disabled={submitting}
                        className="rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90 disabled:opacity-50"
                    >
                        {submitting ? '저장 중…' : '저장'}
                    </button>
                )}
            </form>
        </div>
    )
}
